"use client";

import {useEffect, useRef} from "react";

function getOrCreateSessionId(): string {
    const key = "__casago_session_id__";
    let id = sessionStorage.getItem(key);
    if (id === null) {
        id = crypto.randomUUID();
        sessionStorage.setItem(key, id);
    }
    return id;
}

async function sendErrorToServer(payload: {code: string; message: string; stack?: string}): Promise<void> {
    try {
        await fetch("/log-frontend-error", {
            method: "POST",
            headers: {"Content-Type": "application/json"},
            body: JSON.stringify({
                ...payload,
                clientSessionId: getOrCreateSessionId(),
                url: window.location.href,
                route: window.location.pathname,
                timestamp: new Date().toISOString(),
                userAgent: navigator.userAgent,
            }),
        });
    } catch {
        // Silent — never throw from error reporter
    }
}

export function useFrontendErrorCapture(): void {
    const initialized = useRef(false);

    useEffect(() => {
        if (initialized.current) return;
        initialized.current = true;

        const onError = (event: ErrorEvent): void => {
            void sendErrorToServer({
                code: "FRONTEND_UNCAUGHT_ERROR",
                message: event.message,
                stack: (event.error as Error | null)?.stack,
            });
        };

        const onUnhandledRejection = (event: PromiseRejectionEvent): void => {
            const reason: unknown = event.reason;
            void sendErrorToServer({
                code: "FRONTEND_UNHANDLED_REJECTION",
                message: reason instanceof Error ? reason.message : String(reason),
                stack: reason instanceof Error ? reason.stack : undefined,
            });
        };

        window.addEventListener("error", onError);
        window.addEventListener("unhandledrejection", onUnhandledRejection);

        return () => {
            window.removeEventListener("error", onError);
            window.removeEventListener("unhandledrejection", onUnhandledRejection);
        };
    }, []);
}

export function FrontendErrorCapture(): null {
    useFrontendErrorCapture();
    return null;
}
